import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Flag, MessageCircle } from "lucide-react";
import { YStack } from "tamagui";

import ReportSheet from "@/components/ReportSheet";
import { MemberPageScaffold, MemberPill } from "@/components/MemberScaffold";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { formatPublicLocation } from "@/lib/locationPrivacy";
import { dispatchNotification } from "@/lib/pushNotifications";
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppErrorState,
  AppLoadingState,
  AppSection,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";
import {
  DISCOVERY_CHILD_AGE_LABELS,
  FUTURE_MEMBER_VERIFICATION,
  LOOKING_FOR_LABELS,
  PROFILE_AVAILABILITY_LABELS,
  PROFILE_CUSTODY_LABELS,
  PROFILE_INTEREST_LABELS,
} from "../../shared/community";

type ProfileRow = Pick<
  Tables<"profiles">,
  "id" | "display_name" | "postcode" | "bio" | "avatar_url" | "looking_for" | "interests" | "availability" | "custody_arrangement" | "created_at"
>;
type ConnectionRow = Tables<"connections">;

const MemberProfile = () => {
  const { id: memberId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<ProfileRow | null>(null);
  const [childAgeBands, setChildAgeBands] = useState<string[]>([]);
  const [connection, setConnection] = useState<ConnectionRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      if (!memberId || !user) return;

      const [{ data: nextProfile }, { data: nextChildAgeBands }, { data: nextConnections }] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, display_name, postcode, bio, avatar_url, looking_for, interests, availability, custody_arrangement, created_at")
          .eq("id", memberId)
          .single(),
        supabase.from("member_child_age_bands").select("age_bands").eq("user_id", memberId).maybeSingle(),
        supabase
          .from("connections")
          .select("*")
          .or(`and(requester_id.eq.${user.id},recipient_id.eq.${memberId}),and(requester_id.eq.${memberId},recipient_id.eq.${user.id})`),
      ]);

      setProfile((nextProfile ?? null) as ProfileRow | null);
      setChildAgeBands(nextChildAgeBands?.age_bands ?? []);
      setConnection(((nextConnections ?? [])[0] ?? null) as ConnectionRow | null);
      setLoading(false);
    };

    void fetch();
  }, [memberId, user]);

  const connect = async () => {
    if (!user || !memberId) return;

    const { error } = await supabase.from("connections").insert({ requester_id: user.id, recipient_id: memberId });

    if (error) {
      toast({ title: "Couldn't send request", variant: "destructive" });
      return;
    }

    const { data: myProfile } = await supabase.from("profiles").select("display_name").eq("id", user.id).single();
    await dispatchNotification({
      userId: memberId,
      type: "connection_request",
      body: `${myProfile?.display_name ?? "Someone"} sent you a connection request`,
      url: `/community/member/${user.id}`,
      entityType: "connection",
      entityId: user.id,
    });

    toast({ title: "Connection request sent" });
    const { data } = await supabase
      .from("connections")
      .select("*")
      .or(`and(requester_id.eq.${user.id},recipient_id.eq.${memberId}),and(requester_id.eq.${memberId},recipient_id.eq.${user.id})`);

    setConnection(((data ?? [])[0] ?? null) as ConnectionRow | null);
  };

  const acceptConnection = async () => {
    if (!connection || !user || !memberId) return;

    await supabase.from("connections").update({ status: "connected" }).eq("id", connection.id);

    const { data: myProfile } = await supabase.from("profiles").select("display_name").eq("id", user.id).single();
    await dispatchNotification({
      userId: connection.requester_id,
      type: "connection_accepted",
      body: `${myProfile?.display_name ?? "Someone"} accepted your connection request`,
      url: `/community/member/${user.id}`,
      entityType: "connection",
      entityId: user.id,
    });

    toast({ title: "Connected!" });
    setConnection({ ...connection, status: "connected" });
  };

  if (loading) {
    return (
      <MemberPageScaffold title="Member profile" fallbackPath="/community/members">
        <AppLoadingState
          title="Loading member"
          body="Getting this member’s profile and connection status ready."
        />
      </MemberPageScaffold>
    );
  }

  if (!profile) {
    return (
      <MemberPageScaffold title="Member profile" fallbackPath="/community/members">
        <AppErrorState
          title="Member not found"
          body="That profile isn’t available right now."
          action={
            <AppButton tone="outline" onPress={() => navigate("/community/members")}>
              Back to nearby members
            </AppButton>
          }
        />
      </MemberPageScaffold>
    );
  }

  const isSelf = memberId === user?.id;
  const lookingFor = profile.looking_for ?? [];
  const interests = profile.interests ?? [];
  const availability = profile.availability ?? [];

  return (
    <MemberPageScaffold
      title={profile.display_name ?? "Member"}
      subtitle="Read a little, go slowly, and only connect if it feels comfortable."
      fallbackPath="/community/members"
      action={
        !isSelf ? (
          <AppButton type="button" tone="outline" size="sm" onPress={() => setReportOpen(true)}>
            <Flag size={14} />
            Report
          </AppButton>
        ) : undefined
      }
    >
      <AppSection>
        <AppCard>
          <AppCardBody alignItems="center" gap="$sm">
            <Avatar className="h-20 w-20">
              {profile.avatar_url ? (
                <AvatarImage src={profile.avatar_url} alt={profile.display_name ?? "Member"} />
              ) : null}
              <AvatarFallback className="bg-primary/20 text-primary text-2xl font-semibold">
                {(profile.display_name ?? "K")[0]}
              </AvatarFallback>
            </Avatar>

            <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$xl" lineHeight="$xl" color="$heading" textAlign="center">
              {profile.display_name ?? "Kindred member"}
            </AppText>

            {profile.postcode ? (
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm" textAlign="center">
                {formatPublicLocation(profile.postcode)}
              </AppText>
            ) : null}

            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm" textAlign="center">
              Member since {new Date(profile.created_at).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
            </AppText>

            {profile.bio ? (
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md" textAlign="center">
                {profile.bio}
              </AppText>
            ) : null}

            {!isSelf ? (
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md" textAlign="center">
                Take your time here. Read a little, see whether this feels like a fit, and only connect if you want to.
              </AppText>
            ) : null}

            <YStack alignItems="center" gap="$xs">
              <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" textTransform="uppercase" letterSpacing={1.2}>
                {FUTURE_MEMBER_VERIFICATION.title}
              </AppText>
              <MemberPill tone="secondary">{FUTURE_MEMBER_VERIFICATION.statusTag}</MemberPill>
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md" textAlign="center">
                {FUTURE_MEMBER_VERIFICATION.body}
              </AppText>
            </YStack>

            {lookingFor.length > 0 ? (
              <YStack alignItems="center" gap="$xs">
                <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" textTransform="uppercase" letterSpacing={1.2}>
                  Looking for
                </AppText>
                <div className="flex flex-wrap justify-center gap-2">
                  {lookingFor.map((value) => (
                    <MemberPill key={value} tone="primary">
                      {LOOKING_FOR_LABELS[value] ?? value}
                    </MemberPill>
                  ))}
                </div>
              </YStack>
            ) : null}

            {interests.length > 0 ? (
              <YStack alignItems="center" gap="$xs">
                <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" textTransform="uppercase" letterSpacing={1.2}>
                  Interests
                </AppText>
                <div className="flex flex-wrap justify-center gap-2">
                  {interests.map((value) => (
                    <MemberPill key={value} tone="primary">
                      {PROFILE_INTEREST_LABELS[value] ?? value}
                    </MemberPill>
                  ))}
                </div>
              </YStack>
            ) : null}

            {availability.length > 0 ? (
              <YStack alignItems="center" gap="$xs">
                <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" textTransform="uppercase" letterSpacing={1.2}>
                  Availability
                </AppText>
                <div className="flex flex-wrap justify-center gap-2">
                  {availability.map((value) => (
                    <MemberPill key={value} tone="secondary">
                      {PROFILE_AVAILABILITY_LABELS[value] ?? value}
                    </MemberPill>
                  ))}
                </div>
              </YStack>
            ) : null}

            {childAgeBands.length > 0 ? (
              <div className="flex flex-wrap justify-center gap-2">
                {childAgeBands.map((band) => (
                  <MemberPill key={band} tone="secondary">
                    Ages {DISCOVERY_CHILD_AGE_LABELS[band] ?? band}
                  </MemberPill>
                ))}
              </div>
            ) : null}

            {profile.custody_arrangement ? (
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md" textAlign="center">
                {PROFILE_CUSTODY_LABELS[profile.custody_arrangement] ?? profile.custody_arrangement}
              </AppText>
            ) : null}
          </AppCardBody>
        </AppCard>

        {!isSelf ? (
          <AppCard>
            <AppCardBody gap="$sm">
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                {connection?.status === "connected"
                  ? "You’re already connected here, so you can message whenever it suits you."
                  : connection?.status === "pending"
                    ? connection.recipient_id === user?.id
                      ? "Take your time. Accept only if this feels like a comfortable fit."
                      : "Your request is there. They can respond in their own time."
                    : "You can read first and connect only if this feels like a comfortable fit."}
              </AppText>
              {connection?.status === "connected" ? (
                <>
                  <MemberPill tone="success">Connected</MemberPill>
                  <AppButton tone="outline" onPress={() => navigate(`/messages/${connection.id}`)}>
                    <MessageCircle size={16} />
                    Message
                  </AppButton>
                </>
              ) : connection?.status === "pending" ? (
                <>
                  <MemberPill tone="neutral">Requested</MemberPill>
                  {connection.recipient_id === user?.id ? (
                    <AppButton onPress={acceptConnection}>
                      Accept connection
                    </AppButton>
                  ) : null}
                </>
              ) : (
                <AppButton onPress={connect}>
                  Connect
                </AppButton>
              )}
              <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                If anything feels off, use Report above and the team can review quietly.
              </AppText>
            </AppCardBody>
          </AppCard>
        ) : null}
      </AppSection>

      <ReportSheet open={reportOpen} onOpenChange={setReportOpen} reportedUserId={memberId} />
    </MemberPageScaffold>
  );
};

export default MemberProfile;
